<template>
    <v-row class="align-center justify-center">
        <v-col cols="12" class="left-align-text mt-n3">
            <label :for="inputId" class="default-label">
                {{ t(label ?? "default.empty") }}
            </label>
        </v-col>
        <v-col cols="12" xs="12" sm="12" class="align-self-start mt-n2">
            <v-autocomplete
                v-model="selectedCountry"
                :id="inputId"
                :name="inputName"
                :items="filteredCountries"
                :data-cy="cypressData"
                :placeholder="t(placeholder ?? 'default.empty')"
                :menu-props="{
                    contentClass: 'country-dropdown-menu',
                }"
                :disabled="isDisabled"
                class="country-autocomplete"
                item-title="name"
                item-value="code"
                density="compact"
                variant="outlined"
                color="primary"
                base-color="slate-grey"
                item-color="black"
                active
                auto-select-first
                hide-details
                @update:model-value="
                    (country: string) => updateCountry(country)
                "
            >
                <!-- defines the layout for the list of options-->
                <template #item="{ internalItem: item, props }">
                    <v-list-item v-bind="props">
                        <template #prepend>
                            <div class="mr-3">
                                <country-flag
                                    :country="item.props.value"
                                    size="normal"
                                    :rounded="true"
                                />
                            </div>
                        </template>
                    </v-list-item>
                </template>

                <!-- defines the layout for the selected country-->
                <template #selection="{ internalItem: item }">
                    <div
                        class="d-flex align-items-center ga-3 text-no-wrap overflow-hidden"
                    >
                        <country-flag
                            :country="item.props.value"
                            size="normal"
                            :rounded="true"
                            class="flex-shrink-0"
                        />
                        <span class="text-truncate align-self-center">
                            {{ item.props.title }}
                        </span>
                    </div>
                </template>
            </v-autocomplete>
        </v-col>
    </v-row>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import CountryFlag from "vue-country-flag-next";
import { useSessionStore } from "@/plugins/store";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const sessionStore = useSessionStore();

const componentProps = defineProps<{
    inputId: string;
    containerStyleClass?: string;
    inputStyleClass?: string;
    labelStyleClass?: string;
    placeholder?: string;
    modelValue: string | null;
    isDisabled?: boolean;
    label?: string;
    inputName?: string;
    cypressData?: string;
}>();

const emit = defineEmits(["update:modelValue"]);
const selectedCountry = ref("");
const filteredCountries: any = ref([]);

const updateCountry = (country: string) => {
    emit("update:modelValue", country);
};

onMounted(async () => {
    // Lazy load country json data to decrease bundle size
    const { countryNamesAndIsoCodes } =
        await import("@esenda/core-ui/constants");

    filteredCountries.value = countryNamesAndIsoCodes;

    /**
     * Populate the country code with the given value if the value is
     * not null or empty when the component initialises. Otherwise, it defaults to
     * GB
     */
    selectedCountry.value = String(
        sessionStore.countryCode || componentProps.modelValue,
    );

    updateCountry(selectedCountry.value);
});
</script>

<style>
.country-selector-overlay .v-list-item-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
}
input#country-selector {
    position: absolute;
    width: 100%;
}
</style>
